/**
 * What a user actually gains by moving from one tier to another.
 *
 * The legacy app hand-listed these bullets per modal, so they drifted from what
 * the plans really sold. Everything here is derived instead: the feature names
 * come from `FEATURE_REQUIREMENTS`, which a contract test pins to the server's
 * entitlements snapshot, and the numbers come from the same offline tables the
 * plan context falls back to. A limit cannot be advertised here without also
 * being served.
 *
 * Only limits with a client-side per-tier table appear. History window, export
 * caps and backtester allowances live solely in the served payload, which
 * describes the CURRENT plan and not the one being pitched, so quoting them
 * would mean a second hand-maintained copy. They are deliberately absent.
 */
import {
  CANONICAL_PLAN_TIERS,
  DEFAULT_MAX_CUSTOM_LAYOUTS,
  DEFAULT_MAX_EXPRESSIONS_PER_TIER,
  DEFAULT_SCANNER_PANELS_PER_LAYOUT,
  DEFAULT_SCANNER_ROW_LIMIT,
  FEATURE_LABELS,
  FEATURE_REQUIREMENTS,
  TIER_ORDER,
  type Feature,
  type PlanTier,
} from '@/context/plan-constraint-utils'

/** Features the higher tier grants that the current one does not. */
export function featuresGained(from: PlanTier, to: PlanTier): Feature[] {
  if (TIER_ORDER[to] <= TIER_ORDER[from]) return []
  return (Object.keys(FEATURE_REQUIREMENTS) as Feature[])
    .filter((feature) => {
      const needed = TIER_ORDER[FEATURE_REQUIREMENTS[feature]]
      return needed > TIER_ORDER[from] && needed <= TIER_ORDER[to]
    })
    .sort((a, b) => FEATURE_LABELS[a].localeCompare(FEATURE_LABELS[b]))
}

/** A numeric allowance that grows between the two tiers. */
export interface LimitGain {
  label: string
  from: number
  to: number
  /** Rendered line, e.g. "Scanner rows: 50 to 150" or "Unlimited custom layouts". */
  text: string
}

/** `one` is the singular, for an unlock of exactly one ("1 custom layout"). */
const NUMERIC_LIMITS: {
  label: string
  noun: string
  one: string
  table: Record<PlanTier, number>
}[] = [
  {
    label: 'Custom layouts',
    noun: 'custom layouts',
    one: 'custom layout',
    table: DEFAULT_MAX_CUSTOM_LAYOUTS,
  },
  {
    label: 'Scanner rows',
    noun: 'scanner rows',
    one: 'scanner row',
    table: DEFAULT_SCANNER_ROW_LIMIT,
  },
  {
    label: 'Expressions',
    noun: 'custom expressions',
    one: 'custom expression',
    table: DEFAULT_MAX_EXPRESSIONS_PER_TIER,
  },
  {
    label: 'Scanner panels per layout',
    noun: 'scanner panels per layout',
    one: 'scanner panel per layout',
    table: DEFAULT_SCANNER_PANELS_PER_LAYOUT,
  },
]

export function limitsGained(from: PlanTier, to: PlanTier): LimitGain[] {
  if (TIER_ORDER[to] <= TIER_ORDER[from]) return []
  const gains: LimitGain[] = []
  for (const { label, noun, one, table } of NUMERIC_LIMITS) {
    const before = table[from]
    const after = table[to]
    if (after <= before) continue
    gains.push({
      label,
      from: before,
      to: after,
      text:
        after === Infinity
          ? `Unlimited ${noun}`
          : before === 0
            ? after === 1
              ? `1 ${one}`
              : `Up to ${after} ${noun}`
            : `${label}: ${before} to ${after}`,
    })
  }
  return gains
}

/**
 * The lowest tier above `from` that grants `feature`, or null when the current
 * plan already has it. This is what a gate should pitch: the cheapest upgrade
 * that actually solves the refusal, not the top of the catalog.
 */
export function tierThatUnlocks(feature: Feature, from: PlanTier): PlanTier | null {
  const needed = FEATURE_REQUIREMENTS[feature]
  return TIER_ORDER[needed] > TIER_ORDER[from] ? needed : null
}

/** The next tier up, for a limit that the current plan already partly grants. */
export function nextTierAbove(from: PlanTier): PlanTier | null {
  return CANONICAL_PLAN_TIERS[TIER_ORDER[from] + 1] ?? null
}

/**
 * What the server sells today (`SELECTABLE_PLANS` in edge-user-api's
 * stripe_mapping.py). Base and Ultimate are deliberately off sale. Stands in
 * until `GET /v2/billing/plans` answers, so a pitch never waits on Stripe.
 */
export const DEFAULT_ON_SALE_TIERS: readonly PlanTier[] = ['basic', 'pro', 'pro_plus']

/**
 * The tiers the plans endpoint is selling, in rank order. An unloaded or empty
 * list reads as the default, never as "nothing is for sale".
 */
export function onSaleTiers(
  plans: readonly { plan: string }[] | null | undefined,
): readonly PlanTier[] {
  const sold = new Set(plans?.map((p) => p.plan))
  const tiers = CANONICAL_PLAN_TIERS.filter((t) => sold.has(t))
  return tiers.length > 0 ? tiers : DEFAULT_ON_SALE_TIERS
}

/**
 * The cheapest tier at or above `floor` that can actually be bought, or null
 * when none can. A pitch for a tier off sale sends the user to a billing page
 * that cannot sell it.
 */
export function cheapestOnSaleFrom(floor: PlanTier, onSale: readonly PlanTier[]): PlanTier | null {
  return (
    CANONICAL_PLAN_TIERS.find((t) => TIER_ORDER[t] >= TIER_ORDER[floor] && onSale.includes(t)) ??
    null
  )
}
